// Large data tables panel.
//
// Sits inside the export builder so the full row-level datasets can be
// downloaded on their own, without composing a document. Each table shows its
// live row count and a download button per format (CSV / Excel / JSON / PDF).
// The composed document caps tables for readability; this panel is where the
// complete data lives, so the two never fight over "is this everything?".
//
// Rows are pulled lazily on download (getRows) so the panel itself stays
// cheap even when a table holds a few thousand rows.
import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography, Button, Tooltip } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import MainCard from '../../../components/cards/MainCard';
import {
    LARGE_TABLES, TABLE_FORMATS, TABLE_PERIODS, TABLE_ROW_CAP,
    downloadBlob, largeTableRequest, largeTableLiveRows, isLazyLargeTable,
} from '../exportData';
import useDashboardData, { tableDateRange } from '../hooks/useDashboardData';
import { PeriodPicker } from './ExportBuilderPage';
import { useGetDwmBreachedUsersQuery } from '../../../store/services/dwm-service';
import { useAppendExportHistoryMutation } from '../../../store/services/reportLayout.service';
import { generateTable } from '../exportGenerators';
import useFeature from '../useFeature';

export default function LargeTablesPanel({ onToast, scope }) {
    const dwmOn = useFeature('darkWebMonitoring');
    const { tenancyId, tenancyName } = useSelector((state) => state.tenancy);
    const brand = (tenancyName || '').trim() || 'Boxphish';
    const [busy, setBusy] = useState(null);
    const [appendHistory] = useAppendExportHistoryMutation();
    const tables = LARGE_TABLES.filter((t) => !t.feature || (t.feature === 'darkWebMonitoring' ? dwmOn : true));

    // Every table comes from the server for the panel's own period, which
    // defaults to all time (BD-1124). The DWM table pulls every breached email
    // from the dwm-api, joined to the roster for name/department. A Tenancy
    // filter is forwarded so the selected child tenancies are pooled in.
    const tenancyScope = Array.isArray(scope?.tenancy) && scope.tenancy.length ? { tenancy: scope.tenancy } : undefined;
    const { period, setPeriod, customStart, setCustomStart, customEnd, setCustomEnd, customReady, dateWindow } = useTableWindow();
    const request = (id) => largeTableRequest(id, tenancyScope, dateWindow);

    const [requested, setRequested] = useState(() => new Set());
    const [pending, setPending] = useState(null);
    const rosterQ = useDashboardData(request('users'), { skip: !customReady });
    const simEventsQ = useDashboardData(request('send-history'), { skip: !customReady || !requested.has('send-history') });
    const courseEventsQ = useDashboardData(request('course-sends'), { skip: !customReady || !requested.has('course-sends') });
    const simsQ = useDashboardData(request('all-sims'), { skip: !customReady });
    const dwmQ = useGetDwmBreachedUsersQuery({ tenancyId, page: 1, limit: 1000 }, { skip: !dwmOn || !tenancyId });
    // currentData, not data: data keeps the previous window's rows while a new
    // window loads, which would export them under the new period.
    const liveRows = useMemo(() => {
        const roster = Array.isArray(rosterQ.currentData?.roster) ? rosterQ.currentData.roster : null;
        const dwm = Array.isArray(dwmQ.data?.users) && roster ? joinDwmToRoster(dwmQ.data.users, roster) : null;
        return {
            users: roster, 'high-risk': roster, 'dwm-by-email': dwm,
            'send-history': largeTableLiveRows('send-history', simEventsQ.currentData),
            'course-sends': largeTableLiveRows('course-sends', courseEventsQ.currentData),
            'all-sims': largeTableLiveRows('all-sims', simsQ.currentData),
        };
    }, [rosterQ.currentData, dwmQ.data, simEventsQ.currentData, courseEventsQ.currentData, simsQ.currentData]);

    const TABLE_QUERIES = { users: rosterQ, 'high-risk': rosterQ, 'dwm-by-email': dwmQ, 'send-history': simEventsQ, 'course-sends': courseEventsQ, 'all-sims': simsQ };

    // Every download waits for server rows; there is no browser fallback.
    const handleDownload = (table, format) => {
        setBusy(`${table.id}-${format}`);
        if (!Array.isArray(liveRows[table.id])) {
            if (isLazyLargeTable(table.id)) setRequested((prev) => new Set(prev).add(table.id));
            setPending({ table, format });
            return;
        }
        runDownload(table, format);
    };

    const pendingQ = pending ? TABLE_QUERIES[pending.table.id] : null;
    useEffect(() => {
        if (!pending) return;
        if (Array.isArray(liveRows[pending.table.id])) {
            const { table, format } = pending;
            setPending(null);
            runDownload(table, format);
        } else if (pendingQ?.isError) {
            setPending(null);
            setBusy(null);
            onToast && onToast(`Could not load ${pending.table.label}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pending, liveRows, pendingQ?.isError]);

    const runDownload = (table, format) => {
        // Defer so the button shows its pressed state before a large PDF blocks
        // the main thread; keeps the click feeling responsive.
        setTimeout(() => {
            try {
                const rows = table.getRows(liveRows[table.id]);
                const { content, mime, filename } = generateTable(format, table.label, table.columns, rows, { brand });
                downloadBlob(content, mime, filename);
                appendHistory({ tenancyId, entry: {
                    kind: 'table', via: 'download', title: table.label, format, tableId: table.id, rows: rows.length, scopeQuery: tenancyScope,
                    period, ...dateWindow,
                } });
                onToast && onToast(liveRows[table.id].length >= TABLE_ROW_CAP
                    ? `Downloaded ${filename}, but it stopped at the ${TABLE_ROW_CAP.toLocaleString()}-row limit. Pick a shorter period to get every row.`
                    : `Downloaded ${filename} (${rows.length.toLocaleString()} rows)`);
            } finally {
                setBusy(null);
            }
        }, 10);
    };

    return (
        <MainCard content={false} border={false} boxShadow sx={{ bgcolor: 'background.paper' }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableChartOutlinedIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.primary' }}>
                        Large data tables
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: 'text.secondary' }}>
                        Download a full dataset on its own, in any format. {tables.length} tables available.
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ px: 3, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ maxWidth: 360 }}>
                    <PeriodPicker
                        options={TABLE_PERIODS} period={period} start={customStart} end={customEnd}
                        onPeriod={setPeriod} onStart={setCustomStart} onEnd={setCustomEnd}
                    />
                </Box>
            </Box>

            <Box sx={{ p: 1.5 }}>
                {tables.map((t) => {
                    return (
                        <Box
                            key={t.id}
                            sx={{
                                display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
                                px: 1.5, py: 1.25, borderRadius: 1.5,
                                '&:hover': { bgcolor: 'action.hover' },
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 180 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'text.primary' }}>{t.label}</Typography>
                                    <Typography sx={{ fontSize: 9.5, fontWeight: 600, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em', border: '1px solid', borderColor: 'divider', borderRadius: 999, px: 0.75 }}>{t.source}</Typography>
                                </Box>
                                <Typography sx={{ fontSize: 10.5, color: 'text.secondary' }}>{t.description}</Typography>
                                <Typography sx={{ fontSize: 9.5, color: 'text.disabled' }}>{rowCountText(t, liveRows[t.id])} · {t.columns.length} columns</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {TABLE_FORMATS.map((f) => {
                                    const isBusy = busy === `${t.id}-${f}`;
                                    return (
                                        <Tooltip key={f} title={`Download ${t.label} as ${f}`}>
                                            <span>
                                                <Button
                                                    size="small" variant="outlined"
                                                    onClick={() => handleDownload(t, f)}
                                                    disabled={!!busy || !customReady}
                                                    startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 13 }} />}
                                                    sx={{
                                                        minWidth: 0, fontSize: 10.5, fontWeight: 600, px: 1, py: '2px', borderRadius: '4px',
                                                        textTransform: 'none', borderColor: 'divider', color: 'text.secondary',
                                                        '& .MuiButton-startIcon': { mr: 0.5 },
                                                        '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' },
                                                    }}
                                                >
                                                    {isBusy ? '...' : f}
                                                </Button>
                                            </span>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </MainCard>
    );
}

// The panel's own period, defaulting to all time, and the dates it resolves to.
function useTableWindow() {
    const [period, setPeriod] = useState('all');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const customReady = period !== 'custom' || Boolean(customStart && customEnd);
    const dateWindow = useMemo(() => {
        const { start, end } = tableDateRange({ period, startDate: customStart, endDate: customEnd });
        return { startDate: start, endDate: end };
    }, [period, customStart, customEnd]);
    return { period, setPeriod, customStart, setCustomStart, customEnd, setCustomEnd, customReady, dateWindow };
}

// Every breached email from the dwm-api, most-mentioned first, with name and
// department from the roster.
function joinDwmToRoster(users, roster) {
    const byEmail = new Map(roster.map((r) => [String(r.email || '').toLowerCase(), r]));
    return [...users]
        .sort((a, b) => (b.breaches ?? 0) - (a.breaches ?? 0))
        .map((u) => {
            const match = byEmail.get(String(u.email || '').toLowerCase());
            return {
                email: u.email,
                name: match ? `${match.firstName || ''} ${match.lastName || ''}`.trim() : '',
                dept: match?.dept || '-',
                mentions: u.breaches ?? 0,
                withCreds: u.passwordAvailable ?? 0,
                sources: '-',
                latest: u.latestBreachAt
                    ? new Date(`${u.latestBreachAt}T00:00:00Z`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                    : '-',
            };
        });
}

function rowCountText(table, live) {
    if (Array.isArray(live)) return `${table.getRows(live).length.toLocaleString()} rows`;
    return isLazyLargeTable(table.id) ? 'Rows load on download' : 'Loading rows...';
}
